import { useEffect, useMemo } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import type { SupabaseClient } from "@supabase/supabase-js";

import { AuthProvider, useAuth } from "./auth/AuthProvider";
import { ConsoleAccessGate } from "./auth/ConsoleAccessGate";
import { PasswordReset, SignIn } from "./auth/SignIn";
import { createAnalytics } from "./lib/analytics";
import { AnalyticsProvider, useAnalytics } from "./lib/analytics-context";
import { readRuntimeConfig, type LiveConfig } from "./lib/live-config";
import { createErrorReporter } from "./lib/sentry";
import { ErrorReporterProvider, useErrorReporter } from "./lib/sentry-context";
import { createSupabaseClient } from "./lib/supabase-client";
import { AppShell } from "./nav/AppShell";
import { LegacyInboxRedirect } from "./nav/LegacyInboxRedirect";
import { AdminPage } from "./pages/AdminPage";
import { CommandCenterPage } from "./pages/CommandCenterPage";
import { InboxPage } from "./pages/InboxPage";
import { IncidentsPage } from "./pages/IncidentsPage";
import { PatrolsPage } from "./pages/PatrolsPage";
import { PlaceholderPage } from "./pages/PlaceholderPage";
import { PropertyMapPage } from "./pages/PropertyMapPage";
import { SetupPage } from "./pages/SetupPage";
import { HelpPage } from "./pages/HelpPage";
import { PlatformPage } from "./pages/PlatformPage";
import { ReportsPage } from "./pages/ReportsPage";
import { SchedulePage } from "./pages/SchedulePage";
import { SupportSessionProvider } from "./support/SupportSessionProvider";
import { UpdateBanner, UpdateBlock } from "./update/UpdateNotice";
import { useUpdateControl } from "./update/useUpdateControl";
import { parseAppVersion } from "./update/version";
import { BrandMark } from "./components/BrandMark";
import { startClientErrorCodeCapture } from "./lib/client-error-codes";

function marketingStamp(raw: string): string {
  const { version } = parseAppVersion(raw);
  return version.length > 0 ? version : "26.08.0001";
}
// Gated layers: runtime-config gate -> auth gate (stay-logged-in) -> update-control + routed shell.
export function App() {
  const configResult = useMemo(() => readRuntimeConfig(), []);
  if (!configResult.ok) {
    return <ConfigNotReady problems={configResult.problems ?? [configResult.reason]} />;
  }
  return <ConfiguredApp config={configResult.config} />;
}

function ConfiguredApp({ config }: { config: LiveConfig }) {
  const client = useMemo(() => createSupabaseClient(config), [config]);
  // Allowlist-only analytics — a frozen no-op unless a runtime phc_ key was injected. When the
  // tenant flag resolver lands (Codex), this also gets a per-account analytics flag like guard-web.
  const analytics = useMemo(
    () => createAnalytics({ key: config.posthogKey, host: config.posthogHost, surface: "console" }),
    [config.posthogKey, config.posthogHost],
  );
  // Crash/error reporting — frozen no-op unless a runtime Sentry DSN was injected. Replay/traces off.
  const errorReporter = useMemo(
    () =>
      createErrorReporter({
        dsn: config.sentryDsn,
        environment: config.sentryEnvironment,
        surface: "console",
        release: __APP_VERSION__,
      }),
    [config.sentryDsn, config.sentryEnvironment],
  );
  return (
    <ErrorReporterProvider reporter={errorReporter}>
      <AnalyticsProvider analytics={analytics}>
        <AuthProvider client={client}>
          <AuthGate client={client} config={config} />
        </AuthProvider>
      </AnalyticsProvider>
    </ErrorReporterProvider>
  );
}

function AuthGate({ client, config }: { client: SupabaseClient; config: LiveConfig }) {
  const { recoveryMode, status } = useAuth();
  if (status === "loading") {
    return <BootScreen subtitle="Restoring session…" />;
  }
  if (recoveryMode) {
    return <PasswordReset />;
  }
  if (status === "unauthenticated") {
    return <SignIn />;
  }
  return (
    <ConsoleAccessGate client={client} config={config}>
      <Shell client={client} config={config} />
    </ConsoleAccessGate>
  );
}

function Shell({ client, config }: { client: SupabaseClient; config: LiveConfig }) {
  const { user } = useAuth();
  const actorId = user?.id ?? "unknown";
  const update = useUpdateControl(client, { accountId: config.accountId, actorId });
  const reload = () => window.location.reload();

  // A forced / unsupported version blocks the entire console until reload.
  if (update.level === "hard") {
    return <UpdateBlock message={update.message} onReload={reload} />;
  }

  return (
    <div className="approot">
      {update.level === "soft" ? <UpdateBanner message={update.message} onReload={reload} /> : null}
      {/* Above the router so an act-as session survives navigation: the banner and the pinned
          workspace are the same state on every route, not a per-page decoration. */}
      <SupportSessionProvider client={client}>
        <BrowserRouter>
          <SectionTracker accountId={config.accountId} />
          <Routes>
            <Route element={<AppShell client={client} config={config} />}>
              <Route index element={<CommandCenterPage />} />
              <Route path="inbox/*" element={<InboxPage />} />
              <Route path="patrols/*" element={<PatrolsPage />} />
              <Route path="schedule/*" element={<SchedulePage />} />
              <Route path="setup/property-map" element={<PropertyMapPage />} />
              <Route path="setup/*" element={<SetupPage />} />
              <Route path="feed/*" element={<LegacyInboxRedirect segment="action" />} />
              <Route path="channels/*" element={<LegacyInboxRedirect segment="boards" />} />
              <Route path="incidents/*" element={<IncidentsPage />} />
              <Route path="reports/*" element={<ReportsPage />} />
              <Route path="admin/*" element={<AdminPage />} />
              <Route path="platform/*" element={<PlatformPage />} />
              <Route path="help/*" element={<HelpPage />} />
              <Route path="*" element={<PlaceholderPage title="Not found" />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </SupportSessionProvider>
    </div>
  );
}

/** Emits app_booted + identify once, then section_viewed per top-level section change. The
 *  section name is the route's first path segment — a fixed nav vocabulary, never user data. */
function SectionTracker({ accountId }: { accountId: string }) {
  const analytics = useAnalytics();
  const errorReporter = useErrorReporter();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const section = pathname.split("/").filter(Boolean)[0] ?? "command-center";
  const userId = user?.id;

  useEffect(() => startClientErrorCodeCapture(window), []);

  useEffect(() => {
    if (userId !== undefined) {
      const accountTail = accountId.slice(-4);
      analytics.identify(userId, accountTail);
      errorReporter.identify(userId, accountTail);
    }
    analytics.capture("app_booted", {
      appVersion: __APP_VERSION__,
      networkOnline: typeof navigator === "undefined" || navigator.onLine !== false,
    });
  }, [analytics, errorReporter, userId, accountId]);

  useEffect(() => {
    analytics.capture("section_viewed", { section });
  }, [analytics, section]);

  return null;
}

function ConfigNotReady({ problems }: { problems: string[] }) {
  return (
    <div className="boot">
      <main className="boot__card" role="main" aria-labelledby="boot-title">
        <BrandMark className="boot__mark" />
        <h1 id="boot-title" className="boot__title">
          SecOps Command Center
        </h1>
        <p className="boot__tagline">Runtime configuration not loaded.</p>
        <p className="boot__reason">
          This deployment is missing required public configuration. Fix the items below and
          reload - no values are ever shown here.
        </p>
        <ul className="boot__problems" aria-label="Configuration problems">
          {problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
        <p className="boot__version" aria-label="Application version">
          v{marketingStamp(__APP_VERSION__)}
        </p>
      </main>
    </div>
  );
}

function BootScreen({ subtitle }: { subtitle: string }) {
  return (
    <div className="boot">
      <main className="boot__card" role="main" aria-labelledby="boot-title">
        <BrandMark className="boot__mark" />
        <h1 id="boot-title" className="boot__title">
          SecOps Command Center
        </h1>
        <p className="boot__tagline">{subtitle}</p>
        <p className="boot__version" aria-label="Application version">
          v{marketingStamp(__APP_VERSION__)}
        </p>
      </main>
    </div>
  );
}
